import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  Alert,
  IconButton,
  Fade,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  ConfirmationNumber as TicketIcon,
  AccessTime as TimeIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { motion, useScroll, useTransform } from 'framer-motion';
import eventService from '../../services/eventService';
import RSVPDialog from '../../components/RSVPDialog';
import { DetailSkeleton } from '../../components/common/LoadingSkeleton';
import useScrollReveal from '../../hooks/useScrollReveal';
import useParallax from '../../hooks/useParallax';

const EventDetailPublic = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State management
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rsvpDialogOpen, setRsvpDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Parallax effect for banner
  const parallaxOffset = useParallax(0.5);

  // Scroll reveal hooks for different sections
  const heroReveal = useScrollReveal({ threshold: 0.1 });
  const contentReveal = useScrollReveal({ threshold: 0.2 });
  const detailsReveal = useScrollReveal({ threshold: 0.2 });

  // Fetch event data
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await eventService.getEventBySlug(slug);
        if (response.success) {
          setEvent(response.data.event);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch event');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [slug]);

  // Helper functions
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'EEEE, MMMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const isEventFull = () => {
    if (!event || event.capacity === 0) return false;
    return event.totalRSVPs >= event.capacity;
  };

  const getSpotsRemaining = () => {
    if (!event || event.capacity === 0) return 'Unlimited';
    const remaining = event.capacity - (event.totalRSVPs || 0);
    return remaining > 0 ? remaining : 0;
  };

  const getCapacityPercentage = () => {
    if (!event || event.capacity === 0) return 0;
    return Math.min((event.totalRSVPs / event.capacity) * 100, 100);
  };

  // Event handlers
  const handleRSVPSuccess = (rsvp) => {
    setEvent((prev) => ({
      ...prev,
      totalRSVPs: (prev.totalRSVPs || 0) + 1 + (rsvp.additionalGuests || 0),
    }));

    setSuccessMessage('Registration successful! Check your email for confirmation.');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    setTimeout(() => {
      setSuccessMessage('');
    }, 8000);
  };

  const handleOpenRSVPDialog = () => {
    setRsvpDialogOpen(true);
  };

  const handleCloseRSVPDialog = () => {
    setRsvpDialogOpen(false);
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <DetailSkeleton />
        </Container>
      </Box>
    );
  }

  // Error state
  if (error || !event) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Fade in timeout={500}>
            <Box>
              <Alert severity="error" sx={{ mb: 3 }}>
                {error || 'Event not found'}
              </Alert>
              <Button
                startIcon={<BackIcon />}
                onClick={() => navigate('/events')}
                variant="outlined"
                size="large"
              >
                Back to Events
              </Button>
            </Box>
          </Fade>
        </Container>
      </Box>
    );
  }

  // Main render
  return (
    <Box sx={{ bgcolor: '#fafafa', minHeight: '100vh', pb: { xs: 10, md: 8 } }}>
      {/* Success Message - Fixed at top */}
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          style={{ position: 'fixed', top: 20, left: 0, right: 0, zIndex: 1300 }}
        >
          <Container maxWidth="lg">
            <Alert
              severity="success"
              icon={<CheckIcon />}
              action={
                <IconButton
                  size="small"
                  onClick={() => setSuccessMessage('')}
                  sx={{ color: 'inherit' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                fontWeight: 500,
              }}
            >
              {successMessage}
            </Alert>
          </Container>
        </motion.div>
      )}

      {/* Hero Banner with Parallax */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '40vh', md: '60vh' },
          overflow: 'hidden',
          bgcolor: 'grey.900',
        }}
      >
        {/* Parallax Background Image */}
        {event.banner?.url && (
          <Box
            component={motion.div}
            style={{
              y: parallaxOffset,
            }}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              transform: 'translateY(-10%)',
            }}
          >
            <img
              src={eventService.getImageUrl(event.banner.url)}
              alt={event.eventName}
              style={{
                width: '100%',
                height: '120%',
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
          </Box>
        )}

        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)',
          }}
        />

        {/* Event Title Overlay */}
        <Container maxWidth="lg" sx={{ position: 'relative', height: '100%' }}>
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: 20, md: 40 },
              left: { xs: 16, md: 0 },
              right: { xs: 16, md: 0 },
            }}
          >
            <Container maxWidth="lg" sx={{ px: { xs: 0, md: 3 } }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: 3,
                    p: { xs: 3, md: 4 },
                    border: '1px solid rgba(255,255,255,0.2)',
                    maxWidth: { xs: '100%', md: '70%' },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5} mb={2} flexWrap="wrap">
                    <Chip
                      label={event.category}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.25)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.3)',
                        fontSize: '0.875rem',
                      }}
                    />
                    {event.tags?.slice(0, 3).map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.15)',
                          color: 'white',
                          border: '1px solid rgba(255,255,255,0.2)',
                        }}
                      />
                    ))}
                  </Box>
                  <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                      color: 'white',
                      fontWeight: 800,
                      fontSize: { xs: '2rem', md: '3.5rem' },
                      lineHeight: 1.1,
                      letterSpacing: '-0.02em',
                      textShadow: '0 2px 12px rgba(0,0,0,0.3)',
                    }}
                  >
                    {event.eventName}
                  </Typography>
                </Box>
              </motion.div>
            </Container>
          </Box>
        </Container>
      </Box>

      {/* Main Content - Split Screen Layout */}
      <Container maxWidth="lg" sx={{ mt: { xs: 4, md: 6 }, px: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 400px' },
            gap: 4,
            alignItems: 'start',
          }}
        >
          {/* Left Column - Scrollable Content */}
          <Box>
            {/* Event Description Card */}
            <motion.div
              ref={contentReveal.ref}
              initial={{ opacity: 1, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  mb: 4,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'primary.light',
                    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.08)',
                  },
                }}
              >
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    position: 'relative',
                    display: 'inline-block',
                    '&:after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -8,
                      left: 0,
                      width: '60px',
                      height: '3px',
                      bgcolor: 'primary.main',
                      borderRadius: 2,
                    },
                  }}
                >
                  About This Event
                </Typography>

                {/* Featured Image */}
                {event.banner?.url && (
                  <Box
                    sx={{
                      mt: 4,
                      mb: 4,
                      borderRadius: 2,
                      overflow: 'hidden',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                    }}
                  >
                    <img
                      src={eventService.getImageUrl(event.banner.url)}
                      alt={event.eventName}
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                )}

                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.8,
                    fontSize: '1.125rem',
                    color: 'text.primary',
                  }}
                >
                  {event.description}
                </Typography>
              </Paper>
            </motion.div>

            {/* Event Details Card (Mobile Only) */}
            {isMobile && (
              <motion.div
                ref={detailsReveal.ref}
                initial={{ opacity: 1, y: 0 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <EventDetailsCard
                  event={event}
                  formatDate={formatDate}
                  formatTime={formatTime}
                  getCapacityPercentage={getCapacityPercentage}
                  isEventFull={isEventFull}
                  getSpotsRemaining={getSpotsRemaining}
                />
              </motion.div>
            )}
          </Box>

          {/* Right Column - Sticky Sidebar (Desktop Only) */}
          {!isMobile && (
            <Box
              sx={{
                position: 'sticky',
                top: 90,
              }}
            >
              <motion.div
                ref={detailsReveal.ref}
                initial={{ opacity: 1, x: 0 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <EventDetailsCard
                  event={event}
                  formatDate={formatDate}
                  formatTime={formatTime}
                  getCapacityPercentage={getCapacityPercentage}
                  isEventFull={isEventFull}
                  getSpotsRemaining={getSpotsRemaining}
                />

                {/* RSVP Card */}
                {event.rsvpEnabled && (
                  <motion.div
                    initial={{ opacity: 1, y: 0 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        mt: 3,
                        p: 4,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: '2px solid',
                        borderColor: 'transparent',
                        borderRadius: 3,
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.4s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
                        },
                        '&:before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
                          pointerEvents: 'none',
                        },
                      }}
                    >
                      <TicketIcon sx={{ fontSize: 40, mb: 2, opacity: 0.9 }} />
                      <Typography variant="h5" gutterBottom fontWeight={700} sx={{ mb: 1 }}>
                        Reserve Your Spot
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, opacity: 0.95, lineHeight: 1.6 }}>
                        {isEventFull()
                          ? 'This event has reached maximum capacity'
                          : 'Secure your attendance and receive instant confirmation'}
                      </Typography>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={isEventFull()}
                        onClick={handleOpenRSVPDialog}
                        sx={{
                          bgcolor: 'white',
                          color: 'primary.main',
                          fontWeight: 700,
                          fontSize: '1.125rem',
                          py: 1.75,
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: 'grey.50',
                            transform: 'scale(1.02)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                          },
                          '&:disabled': {
                            bgcolor: 'rgba(255,255,255,0.3)',
                            color: 'rgba(255,255,255,0.7)',
                          },
                        }}
                      >
                        {isEventFull() ? 'Event Full' : 'Register Now'}
                      </Button>
                      {event.rsvpDeadline && !isEventFull() && (
                        <Typography
                          variant="caption"
                          sx={{
                            mt: 2,
                            display: 'block',
                            textAlign: 'center',
                            opacity: 0.85,
                          }}
                        >
                          Registration closes: {formatDate(event.rsvpDeadline)}
                        </Typography>
                      )}
                    </Paper>
                  </motion.div>
                )}
              </motion.div>
            </Box>
          )}
        </Box>
      </Container>

      {/* Mobile Fixed Bottom CTA */}
      {isMobile && event.rsvpEnabled && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            borderRadius: '16px 16px 0 0',
            zIndex: 1200,
            bgcolor: 'white',
          }}
        >
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={isEventFull()}
            onClick={handleOpenRSVPDialog}
            startIcon={<TicketIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              fontWeight: 700,
              fontSize: '1.125rem',
              py: 1.75,
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.02)',
                boxShadow: 4,
              },
            }}
          >
            {isEventFull() ? 'Event Full' : 'Register Now'}
          </Button>
        </Paper>
      )}

      {/* RSVP Dialog */}
      <RSVPDialog
        open={rsvpDialogOpen}
        onClose={handleCloseRSVPDialog}
        event={event}
        onSuccess={handleRSVPSuccess}
      />
    </Box>
  );
};

// Separate component for event details card (reused for mobile/desktop)
const EventDetailsCard = ({
  event,
  formatDate,
  formatTime,
  getCapacityPercentage,
  isEventFull,
  getSpotsRemaining,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: 'primary.light',
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.08)',
        },
      }}
    >
      <Typography variant="h5" gutterBottom fontWeight={700} sx={{ mb: 3 }}>
        Event Details
      </Typography>

      {/* Date & Time */}
      <Box
        sx={{
          display: 'flex',
          gap: 2.5,
          mb: 3,
          p: 2.5,
          borderRadius: 2,
          bgcolor: 'grey.50',
          transition: 'all 0.3s ease',
          '&:hover': {
            bgcolor: 'primary.50',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: 'white',
            flexShrink: 0,
          }}
        >
          <CalendarIcon sx={{ color: 'primary.main', fontSize: 24 }} />
        </Box>
        <Box flex={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
            DATE & TIME
          </Typography>
          <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
            {event.dates?.[0]?.startDate && formatDate(event.dates[0].startDate)}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {event.dates?.[0]?.startTime && formatTime(event.dates[0].startTime)}
              {event.dates?.[0]?.endTime && ` - ${formatTime(event.dates[0].endTime)}`}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Location */}
      <Box
        sx={{
          display: 'flex',
          gap: 2.5,
          mb: 3,
          p: 2.5,
          borderRadius: 2,
          bgcolor: 'grey.50',
          transition: 'all 0.3s ease',
          '&:hover': {
            bgcolor: 'primary.50',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: 'white',
            flexShrink: 0,
          }}
        >
          <LocationIcon sx={{ color: 'primary.main', fontSize: 24 }} />
        </Box>
        <Box flex={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
            LOCATION
          </Typography>
          <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
            {event.location?.venue}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {event.location?.address}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {event.location?.city}
            {event.location?.state && `, ${event.location.state}`}
          </Typography>
        </Box>
      </Box>

    </Paper>
  );
};

export default EventDetailPublic;
